import React from 'react';
import { Box, Stack, useTheme, useMediaQuery, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Button } from '../../../Buttons/Button';
import { LoadingButton } from '../../../Buttons/LoadingButton';

export interface DialogActionButton {
  label: string;
  id?: string;
  variant: 'text' | 'outlined' | 'contained';
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  form?: string;
  isLoading?: boolean;
}

export interface DialogActionsLayoutProps {
  buttons?: DialogActionButton[];
  layout?: 'horizontal-button-pair' | 'button-row' | 'action-row';
  leftButtonCount?: number;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  leftComponent?: React.ReactNode;
  leftAdditionalContent?: React.ReactNode;
  onButtonClick?: (buttonId: string, button: DialogActionButton) => void;
  hasDivider?: boolean;
}

const ActionContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const ContentContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const ResponsiveButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'stretch',
  },
}));

export const DialogActionsLayout: React.FC<DialogActionsLayoutProps> = ({
  buttons = [],
  layout = 'horizontal-button-pair',
  leftButtonCount = 1,
  leftContent,
  rightContent,
  leftComponent,
  leftAdditionalContent,
  onButtonClick,
  hasDivider = false,
}) => {
  const {
    breakpoints,
    custom: { spacing },
  } = useTheme();
  const gapSpacing = spacing?.lg;
  const isSmallDevice = useMediaQuery(breakpoints.down('xs'));

  const handleButtonClick = (button: DialogActionButton) => {
    if (button.onClick) {
      button.onClick();
    }
    if (onButtonClick && button.id) {
      onButtonClick(button.id, button);
    }
  };

  const renderButtons = (buttonList: DialogActionButton[]) => {
    if (buttonList.length === 0) return null;

    if (buttonList.length === 1 || isSmallDevice) {
      return (
        <Stack direction={isSmallDevice ? 'column' : 'row'} spacing={gapSpacing}>
          {buttonList.map((button, index) => {
            return button.isLoading ? (
              <LoadingButton
                key={button.id || index}
                id={button.id}
                variant={button.variant}
                color={button.variant === 'outlined' ? 'secondary' : button.color || 'primary'}
                size={button.size || 'medium'}
                disabled={button.disabled}
                fullWidth={button.fullWidth || isSmallDevice}
                startIcon={button.startIcon}
                endIcon={button.endIcon}
                onClick={() => handleButtonClick(button)}
                type={button.type || 'button'}
                form={button.form}
                isLoading={button.isLoading}
              >
                {button.label}
              </LoadingButton>
            ) : (
              <Button
                key={button.id || index}
                id={button.id}
                variant={button.variant}
                color={button.variant === 'outlined' ? 'secondary' : button.color || 'primary'}
                size={button.size || 'medium'}
                disabled={button.disabled}
                fullWidth={button.fullWidth || isSmallDevice}
                startIcon={button.startIcon}
                endIcon={button.endIcon}
                onClick={() => handleButtonClick(button)}
                type={button.type || 'button'}
                form={button.form}
              >
                {button.label}
              </Button>
            );
          })}
        </Stack>
      );
    }

    return (
      <Box sx={{ display: 'flex', gap: gapSpacing }}>
        {buttonList.map((button, index) => {
          return button.isLoading ? (
            <LoadingButton
              key={button.id || index}
              id={button.id}
              variant={button.variant}
              color={button.variant === 'outlined' ? 'secondary' : button.color || 'primary'}
              size={button.size || 'medium'}
              disabled={button.disabled}
              startIcon={button.startIcon}
              endIcon={button.endIcon}
              onClick={() => handleButtonClick(button)}
              sx={{
                ...(button.fullWidth && { flex: 1 }),
              }}
              type={button.type || 'button'}
              form={button.form}
              isLoading={button.isLoading}
            >
              {button.label}
            </LoadingButton>
          ) : (
            <Button
              key={button.id || index}
              id={button.id}
              variant={button.variant}
              color={button.variant === 'outlined' ? 'secondary' : button.color || 'primary'}
              size={button.size || 'medium'}
              disabled={button.disabled}
              startIcon={button.startIcon}
              endIcon={button.endIcon}
              onClick={() => handleButtonClick(button)}
              sx={{
                ...(button.fullWidth && { flex: 1 }),
              }}
              type={button.type || 'button'}
              form={button.form}
            >
              {button.label}
            </Button>
          );
        })}
      </Box>
    );
  };

  // Render left content
  const renderLeftContent = () => {
    if (leftContent) return leftContent;

    if (leftComponent) {
      if (leftAdditionalContent) {
        return (
          <ContentContainer>
            {leftComponent}
            {leftAdditionalContent}
          </ContentContainer>
        );
      }

      return leftComponent;
    }

    // For horizontal-row layout, split buttons
    if (layout === 'button-row' && buttons.length > leftButtonCount) {
      const leftButtons = buttons.slice(0, leftButtonCount);
      return renderButtons(leftButtons);
    }

    return null;
  };

  // Render right content
  const renderRightContent = () => {
    if (rightContent) return rightContent;

    // For horizontal-row layout, remaining buttons go to the right
    if (layout === 'button-row' && buttons.length > leftButtonCount) {
      const rightButtons = buttons.slice(leftButtonCount);
      return renderButtons(rightButtons);
    }

    // For action-row, render all buttons on the right
    if (layout === 'action-row') {
      return renderButtons(buttons);
    }

    return null;
  };

  // Special handling for single button
  if (buttons.length === 1) {
    const button = buttons[0];
    return (
      <Box sx={{ px: isSmallDevice ? spacing?.['xl'] : spacing?.['3xl'], pb: spacing?.['3xl'] }}>
        {hasDivider && <Divider sx={{ color: '#EAECF0', mb: spacing?.['4xl'] }} />}
        <ResponsiveButtonContainer>
          <Button
            variant="contained"
            color={button.variant === 'outlined' ? 'secondary' : button.color || 'primary'}
            size={button.size || 'medium'}
            disabled={button.disabled}
            fullWidth={isSmallDevice}
            startIcon={button.startIcon}
            endIcon={button.endIcon}
            onClick={() => handleButtonClick(button)}
            sx={{
              minWidth: isSmallDevice ? '100%' : 'auto',
            }}
            type={button.type || 'button'}
            form={button.form}
          >
            {button.label}
          </Button>
        </ResponsiveButtonContainer>
      </Box>
    );
  }

  // Layout-specific rendering
  switch (layout) {
    case 'horizontal-button-pair':
      if (isSmallDevice) {
        return (
          <Box>
            {hasDivider && <Divider sx={{ color: '#EAECF0', mb: spacing?.['4xl'] }} />}
            <Box sx={{ px: isSmallDevice ? spacing?.['xl'] : spacing?.['3xl'], pb: spacing?.['3xl'] }}>
              <Stack direction="column" spacing={gapSpacing}>
                {buttons.map((button, index) => {
                  return button.isLoading ? (
                    <LoadingButton
                      key={button.id || index}
                      id={button.id}
                      variant={button.variant}
                      color={button.variant === 'outlined' ? 'secondary' : button.color || 'primary'}
                      size={button.size || 'medium'}
                      disabled={button.disabled}
                      fullWidth={true}
                      startIcon={button.startIcon}
                      endIcon={button.endIcon}
                      onClick={() => handleButtonClick(button)}
                      type={button.type || 'button'}
                      form={button.form}
                      isLoading={button.isLoading}
                    >
                      {button.label}
                    </LoadingButton>
                  ) : (
                    <Button
                      key={button.id || index}
                      id={button.id}
                      variant={button.variant}
                      color={button.variant === 'outlined' ? 'secondary' : button.color || 'primary'}
                      size={button.size || 'medium'}
                      disabled={button.disabled}
                      fullWidth={true}
                      startIcon={button.startIcon}
                      endIcon={button.endIcon}
                      onClick={() => handleButtonClick(button)}
                      type={button.type || 'button'}
                      form={button.form}
                    >
                      {button.label}
                    </Button>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        );
      }
      return (
        <Box>
          {hasDivider && <Divider sx={{ color: '#EAECF0', mb: spacing?.['4xl'] }} />}
          <Box sx={{ px: isSmallDevice ? spacing?.['xl'] : spacing?.['3xl'], pb: spacing?.['3xl'] }}>
            <Stack direction="row" spacing={gapSpacing}>
              {buttons.map((button, index) => {
                return button.isLoading ? (
                  <LoadingButton
                    key={button.id || index}
                    id={button.id}
                    variant={button.variant}
                    color={button.variant === 'outlined' ? 'secondary' : button.color || 'primary'}
                    size={button.size || 'medium'}
                    disabled={button.disabled}
                    fullWidth={button.fullWidth}
                    startIcon={button.startIcon}
                    endIcon={button.endIcon}
                    onClick={() => handleButtonClick(button)}
                    sx={{
                      flex: 1,
                    }}
                    type={button.type || 'button'}
                    form={button.form}
                    isLoading={button.isLoading}
                  >
                    {button.label}
                  </LoadingButton>
                ) : (
                  <Button
                    key={button.id || index}
                    id={button.id}
                    variant={button.variant}
                    color={button.variant === 'outlined' ? 'secondary' : button.color || 'primary'}
                    size={button.size || 'medium'}
                    disabled={button.disabled}
                    fullWidth={button.fullWidth}
                    startIcon={button.startIcon}
                    endIcon={button.endIcon}
                    onClick={() => handleButtonClick(button)}
                    sx={{
                      flex: 1,
                    }}
                    type={button.type || 'button'}
                    form={button.form}
                  >
                    {button.label}
                  </Button>
                );
              })}
            </Stack>
          </Box>
        </Box>
      );

    case 'action-row':
    case 'button-row': {
      const leftContentElement = renderLeftContent();
      const rightContentElement = renderRightContent();

      if (isSmallDevice && !leftContentElement && !rightContentElement) {
        return (
          <Box>
            {hasDivider && <Divider sx={{ color: '#EAECF0', mb: spacing?.['4xl'] }} />}
            <Box
              sx={{
                px: isSmallDevice ? spacing?.['xl'] : spacing?.['3xl'],
                pb: spacing?.['3xl'],
                mt: hasDivider ? 0 : spacing?.['3xl'],
              }}
            >
              <Stack direction="column" spacing={gapSpacing}>
                {buttons.map((button, index) => {
                  return button.isLoading ? (
                    <LoadingButton
                      key={button.id || index}
                      id={button.id}
                      variant={button.variant}
                      color={button.variant === 'outlined' ? 'secondary' : button.color || 'primary'}
                      size={button.size || 'medium'}
                      disabled={button.disabled}
                      fullWidth={true}
                      startIcon={button.startIcon}
                      endIcon={button.endIcon}
                      onClick={() => handleButtonClick(button)}
                      type={button.type || 'button'}
                      form={button.form}
                      isLoading={button.isLoading}
                    >
                      {button.label}
                    </LoadingButton>
                  ) : (
                    <Button
                      key={button.id || index}
                      id={button.id}
                      variant={button.variant}
                      color={button.variant === 'outlined' ? 'secondary' : button.color || 'primary'}
                      size={button.size || 'medium'}
                      disabled={button.disabled}
                      fullWidth={true}
                      startIcon={button.startIcon}
                      endIcon={button.endIcon}
                      onClick={() => handleButtonClick(button)}
                      type={button.type || 'button'}
                      form={button.form}
                    >
                      {button.label}
                    </Button>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        );
      } else if (isSmallDevice && (leftContentElement || rightContentElement)) {
        return (
          <Box>
            {hasDivider && <Divider sx={{ color: '#EAECF0', mb: spacing?.['4xl'] }} />}
            <Box
              sx={{
                px: isSmallDevice ? spacing?.['xl'] : spacing?.['3xl'],
                pb: spacing?.['3xl'],
                mt: hasDivider ? 0 : spacing?.['3xl'],
              }}
            >
              <Stack direction="column" spacing={gapSpacing}>
                {leftContentElement}
                {rightContentElement}
              </Stack>
            </Box>
          </Box>
        );
      }

      return (
        <Box>
          {hasDivider && <Divider sx={{ color: '#EAECF0', mb: spacing?.['4xl'] }} />}
          <ActionContainer
            sx={{
              px: isSmallDevice ? spacing?.['xl'] : spacing?.['3xl'],
              pb: spacing?.['3xl'],
              mt: hasDivider ? 0 : spacing?.['3xl'],
            }}
          >
            <ContentContainer>{leftContentElement}</ContentContainer>
            <ContentContainer>{rightContentElement}</ContentContainer>
          </ActionContainer>
        </Box>
      );
    }
    default:
      return null;
  }
};
